import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, BadgeCheck, Megaphone, MessageSquare, ShieldX, XCircle } from "lucide-react";
import { api, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const ICONS = {
  withdraw_requested: [ArrowUpRight, "text-amber-300"], withdraw_approved: [BadgeCheck, "text-emerald-400"], withdraw_sent: [ArrowUpRight, "text-emerald-400"],
  withdraw_rejected: [XCircle, "text-red-400"], withdraw_failed: [XCircle, "text-red-400"], deposit_ok: [ArrowDownLeft, "text-emerald-400"],
  deposit_failed: [XCircle, "text-red-400"], kyc_approved: [BadgeCheck, "text-emerald-400"], kyc_rejected: [ShieldX, "text-red-400"],
  announcement: [Megaphone, "text-amber-400"], admin: [MessageSquare, "text-sky-300"],
};

const content = (m, t, lang) => {
  const p = m.params || {};
  if (m.kind === "announcement") return [t("inbox.k.announcement"), (lang === "en" && p.en) || p.fr];
  if (m.kind === "admin") return [p.title, p.body];
  const v = { a: formatXAF(p.amount || 0, lang), p: p.phone || "", r: p.reason || "" };
  return [t(`inbox.k.${m.kind}.t`, v), t(`inbox.k.${m.kind}.b`, v)];
};

const Item = ({ m }) => {
  const { t, lang } = useI18n();
  const [Icon, color] = ICONS[m.kind] || ICONS.admin;
  const [title, body] = content(m, t, lang);
  return (
    <li className={`surface flex gap-3 p-4 ${m.read ? "" : "border border-amber-500/40"}`} data-testid={`inbox-item-${m.id}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${color}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="font-semibold" data-testid="inbox-item-title">{title}</p>
          {!m.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-400" data-testid="inbox-item-unread" />}
        </div>
        <p className="mt-1 whitespace-pre-line break-words text-sm text-slate-300" data-testid="inbox-item-body">{body}</p>
        <p className="mt-2 text-xs text-slate-500">{new Date(m.created_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR", { dateStyle: "medium", timeStyle: "short" })}</p>
      </div>
    </li>
  );
};

export default function MessagesPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["inbox"], queryFn: () => api.get("/inbox").then((r) => r.data) });
  const unread = data?.unread || 0;
  useEffect(() => {
    if (unread > 0 && data?.now) {
      api.post("/inbox/read", { until: data.now }).then(() => qc.invalidateQueries({ queryKey: ["inbox-unread"] })).catch(() => {});
    }
  }, [unread, data?.now, qc]);
  return (
    <div className="mx-auto max-w-2xl space-y-5 rise" data-testid="messages-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("inbox.title")}</h1>
        <p className="mt-2 text-sm text-slate-400">{t("inbox.sub", { d: data?.retention_days ?? 30 })}</p>
      </div>
      {data && !data.items.length && <p className="surface p-8 text-center text-sm text-slate-400" data-testid="inbox-empty">{t("inbox.empty")}</p>}
      <ul className="space-y-3" data-testid="inbox-list">
        {(data?.items || []).map((m) => <Item key={m.id} m={m} />)}
      </ul>
    </div>
  );
}
